import { PlusIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { CanvasEditorMachine } from "@/components/canvas-editor/canvas-editor-machine";
import { useToast } from "@/components/hooks/use-toast";
import { PreviewPost } from "@/components/PreviewFeed";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { getHydratedPostQueryOptions, queryClient } from "@/lib/api";
import { insertPosts, removePosts } from "@/lib/feedWrites";
import { SortingOption } from "@/lib/types";
import { useFeedAccessById } from "@/lib/useFeedAccess";
import { apiErrorMessage, cn } from "@/lib/utils";

function Post({ postUrl }: { postUrl: string }) {
  const activePost = useQuery(getHydratedPostQueryOptions(postUrl));

  if (!activePost.data) {
    // Post couldn't be hydrated (e.g. deleted, or an unusual URL) — still show
    // the raw URL so a staged post is never invisible.
    return (
      <div className="border-b border-slate-dim p-2 text-sm text-slate-dim break-all">
        {postUrl}
      </div>
    );
  }

  return (
    <div className={cn("border-b border-slate-dim")}>
      <PreviewPost record={activePost.data} />
    </div>
  );
}

export function AddPostPanel({
  order,
  feedId,
}: {
  order: SortingOption | undefined;
  feedId: number;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [posts, setPosts] = useState<string[]>([]);
  const actor = CanvasEditorMachine.useActorRef();
  const [addToTop, setAddToTop] = useState(false);
  const { toast } = useToast();
  const access = useFeedAccessById(feedId);

  const onAddUrl = () => {
    if (!searchValue.trim()) return;
    setPosts((prev) => Array.from(new Set([...prev, searchValue.trim()])));
    setSearchValue("");
  };

  const onSubmitMutation = useMutation({
    retry: 2,
    onSuccess: (_data, urls: string[]) => {
      setPosts([]);
      setSearchValue("");
      toast({
        title: `Added ${urls.length} post${urls.length === 1 ? "" : "s"} to feed`,
      });
      actor.send({
        type: "SET_VISIBLE_PANEL",
        panel: "feed",
      });
    },
    onError: (e) =>
      toast({
        title: "Failed to add posts to feed",
        description: apiErrorMessage(e),
      }),
    mutationFn: (urls: string[]) =>
      // The feed's own add-post panel, used by whoever is allowed to add -- only the
      // request underneath differs. See `lib/feedWrites.ts`.
      insertPosts({
        feedId,
        postUrls: urls,
        toTop: addToTop,
        asCollaborator: access.isCollaborator,
      }),
  });

  // One-step submit: fold in a URL that was typed but not yet staged via "+",
  // so pasting a URL and pressing "Add" just works.
  const onSubmit = () => {
    const staged = searchValue.trim()
      ? Array.from(new Set([...posts, searchValue.trim()]))
      : posts;
    if (staged.length === 0) return;
    onSubmitMutation.mutate(staged);
  };

  const hidePostsMutation = useMutation({
    onSuccess: () => {
      toast({ title: "Removed posts from feed" });
      setPosts([]);

      actor.send({
        type: "SET_VISIBLE_PANEL",
        panel: "feed",
      });
    },
    onError: (e) =>
      toast({
        title: "Failed to remove posts from feed",
        description: apiErrorMessage(e),
      }),
    mutationFn: async () => {
      if (feedId) {
        await removePosts({
          feedId,
          postUrls: posts,
          asCollaborator: access.isCollaborator,
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
        await queryClient.refetchQueries({
          queryKey: ["feed-preview", feedId],
        });
      }
    },
  });

  return (
    <div className="h-full flex flex-col overflow-hidden canvas-sidebar">
      <div className="flex flex-col gap-6 w-full p-4">
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <Input
              name="post"
              placeholder="Add post to list"
              id="debug-post-url"
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddUrl();
                }
              }}
            />
            <SimpleTooltip message="Add to list">
              <Button
                className="shrink-0"
                id="debug-post-url-button"
                type="button"
                size="icon"
                variant="action"
                onPress={onAddUrl}
                disabled={searchValue.length === 0}
              >
                <span className="sr-only">Add to list</span>
                <PlusIcon />
              </Button>
            </SimpleTooltip>
          </div>
        </div>
      </div>
      <Separator />
      <div className="min-h-0 flex-1 overflow-auto p-4 flex flex-col gap-2">
        {posts.length > 0
          ? posts.map((url) => {
              return (
                <div key={url} className="">
                  <Post postUrl={url} />
                </div>
              );
            })
          : null}
      </div>
      <Separator />
      <div className="flex items-center justify-between py-3 px-4">
        <div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2">
          {order === "new" && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add-to-top"
                checked={addToTop}
                onCheckedChange={(checked) => setAddToTop(Boolean(checked))}
              />
              <label
                htmlFor="add-to-top"
                className="text-sm font-medium leading-none"
              >
                Add to top
              </label>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {access.can("remove_posts") && (
            <Button
              variant="destructive"
              size="sm"
              onPress={() => hidePostsMutation.mutate()}
              disabled={posts.length === 0}
              isLoading={hidePostsMutation.isPending}
            >
              Remove
            </Button>
          )}
          <Button
            size="sm"
            onPress={onSubmit}
            disabled={posts.length === 0 && searchValue.trim().length === 0}
            isLoading={onSubmitMutation.isPending}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
